import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Box, Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ReactMapGL, { Marker, NavigationControl, LinearInterpolator } from "react-map-gl";
import WebMercatorViewport from "viewport-mercator-project";
import { useMapLanguage } from "hooks/useMapLanguage";
import MapboxWorker from "mapbox-gl/dist/mapbox-gl-csp-worker?worker";
import mapboxgl from "mapbox-gl";

mapboxgl.workerClass = MapboxWorker;

const MAPBOX_API_ACCESS_TOKEN = import.meta.env.VITE_MAPBOX_API_ACCESS_TOKEN;
const MAPBOX_MAP_STYLE = import.meta.env.VITE_MAPBOX_WHITE_MAP_STYLE;

const DEFAULT_LATITUDE = 52.516453830917456;
const DEFAULT_LONGITUDE = 13.377655445343715;
const DEFAULT_ZOOM = 8;
const MARKER_ZOOM = 15;
const VIEWPORT_TRANSITION_MS = 300;
const BOUNDS_PADDING = 48;

const usePinTooltipStyles = makeStyles({
  tooltip: {
    backgroundColor: "#000 !important",
    color: "#fff !important",
    fontSize: "15px !important",
    fontWeight: "700 !important",
    padding: "8px 12px !important",
    borderRadius: "4px !important",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.45) !important",
  },
  arrow: {
    color: "#000 !important",
  },
});

const ICON = `M20.2,15.7L20.2,15.7c1.1-1.6,1.8-3.6,1.8-5.7c0-5.6-4.5-10-10-10S2,4.5,2,10c0,2,0.6,3.9,1.6,5.4c0,0.1,0.1,0.2,0.2,0.3
  c0,0,0.1,0.1,0.1,0.2c0.2,0.3,0.4,0.6,0.7,0.9c2.6,3.1,7.4,7.6,7.4,7.6s4.8-4.5,7.4-7.5c0.2-0.3,0.5-0.6,0.7-0.9
  C20.1,15.8,20.2,15.8,20.2,15.7z`;

const pinStyle = {
  fill: "#6F9CEB",
  stroke: "none",
};

function Pin({ size = 20 }) {
  return (
    <svg height={size} viewBox="0 0 24 24" style={pinStyle}>
      <path d={ICON} />
    </svg>
  );
}

function isValidMarker(marker) {
  const lat = Number(marker?.latitude);
  const lng = Number(marker?.longitude);
  return Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0);
}

function withViewportTransition(viewport) {
  return {
    ...viewport,
    transitionDuration: VIEWPORT_TRANSITION_MS,
    transitionInterpolator: new LinearInterpolator(),
    transitionEasing: (t) => t,
  };
}

function viewportForMarkers(markers, width, height) {
  if (!markers.length || width <= 0 || height <= 0) {
    return {
      latitude: DEFAULT_LATITUDE,
      longitude: DEFAULT_LONGITUDE,
      zoom: DEFAULT_ZOOM,
    };
  }

  if (markers.length === 1) {
    return {
      latitude: markers[0].latitude,
      longitude: markers[0].longitude,
      zoom: MARKER_ZOOM,
    };
  }

  const lngs = markers.map((m) => m.longitude);
  const lats = markers.map((m) => m.latitude);
  const mercator = new WebMercatorViewport({ width, height });
  const fitted = mercator.fitBounds(
    [
      [Math.min(...lngs), Math.min(...lats)],
      [Math.max(...lngs), Math.max(...lats)],
    ],
    { padding: BOUNDS_PADDING, maxZoom: MARKER_ZOOM },
  );

  return {
    latitude: fitted.latitude,
    longitude: fitted.longitude,
    zoom: fitted.zoom,
  };
}

const ZipcodeMapPreview = ({
  markers = [],
  height = 500,
  draggable = false,
  onMarkerMove,
}) => {
  const mapboxRef = useRef(null);
  const containerRef = useRef(null);
  const lastFitKeyRef = useRef("");
  const [mapSize, setMapSize] = useState({ width: 0, height });
  useMapLanguage(mapboxRef);
  const pinTooltipClasses = usePinTooltipStyles();
  const tooltipClasses = {
    tooltip: pinTooltipClasses.tooltip,
    arrow: pinTooltipClasses.arrow,
  };

  const visibleMarkers = useMemo(
    () =>
      (markers || []).filter(isValidMarker).map((marker, index) => ({
        ...marker,
        id: marker.zipcode ? `${marker.zipcode}-${index}` : `marker-${index}`,
        latitude: Number(marker.latitude),
        longitude: Number(marker.longitude),
      })),
    [markers],
  );

  const markerFitKey = useMemo(
    () =>
      visibleMarkers
        .map((marker) => String(marker.zipcode ?? ""))
        .sort()
        .join("|"),
    [visibleMarkers],
  );

  const [viewport, setViewport] = useState({
    latitude: DEFAULT_LATITUDE,
    longitude: DEFAULT_LONGITUDE,
    zoom: DEFAULT_ZOOM,
  });

  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) {
      return undefined;
    }

    const updateSize = () => {
      setMapSize({
        width: element.offsetWidth || 0,
        height: element.offsetHeight || height,
      });
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(element);
    return () => observer.disconnect();
  }, [height]);

  useEffect(() => {
    if (!visibleMarkers.length) {
      lastFitKeyRef.current = "";
      return;
    }
    if (mapSize.width <= 0 || mapSize.height <= 0) {
      return;
    }

    const layoutKey = `${markerFitKey}@${mapSize.width}x${mapSize.height}`;
    if (layoutKey === lastFitKeyRef.current) {
      return;
    }
    lastFitKeyRef.current = layoutKey;

    const next = viewportForMarkers(visibleMarkers, mapSize.width, mapSize.height);
    setViewport((prev) => withViewportTransition({ ...prev, ...next }));
  }, [markerFitKey, visibleMarkers, mapSize.width, mapSize.height]);

  const handleViewportChange = (nextViewport) => {
    setViewport(nextViewport);
  };

  const handleTransitionEnd = () => {
    setViewport((prev) => ({
      ...prev,
      transitionDuration: 0,
      transitionInterpolator: undefined,
    }));
  };

  const handleMarkerDrag = (marker) => (event) => {
    if (!onMarkerMove) {
      return;
    }
    const [longitude, latitude] = event.lngLat;
    onMarkerMove(marker.zipcode, latitude, longitude);
  };

  return (
    <Box width="100%" ref={containerRef}>
      <ReactMapGL
        width="100%"
        height={`${height}px`}
        {...viewport}
        onViewportChange={handleViewportChange}
        onTransitionEnd={handleTransitionEnd}
        mapStyle={MAPBOX_MAP_STYLE}
        mapboxApiAccessToken={MAPBOX_API_ACCESS_TOKEN}
        onLoad={(event) => {
          mapboxRef.current = event.target;
          if (visibleMarkers.length && containerRef.current) {
            const width = containerRef.current.offsetWidth;
            const loadHeight = containerRef.current.offsetHeight || mapSize.height;
            if (width > 0 && loadHeight > 0) {
              const next = viewportForMarkers(visibleMarkers, width, loadHeight);
              setViewport((prev) => withViewportTransition({ ...prev, ...next }));
            }
          }
        }}
      >
        <div style={{ position: "absolute", top: 0, left: 0, padding: 10 }}>
          <NavigationControl showCompass={false} />
        </div>
        {visibleMarkers.map((marker) => (
          <Marker
            key={marker.zipcode || marker.id}
            latitude={marker.latitude}
            longitude={marker.longitude}
            offsetTop={-20}
            offsetLeft={-10}
            draggable={draggable}
            onDrag={draggable ? handleMarkerDrag(marker) : undefined}
            onDragEnd={draggable ? handleMarkerDrag(marker) : undefined}
          >
            <Tooltip
              title={marker.zipcode || ""}
              placement="top"
              arrow
              classes={tooltipClasses}
            >
              <div
                style={{
                  cursor: draggable ? "grab" : "default",
                  display: "inline-block",
                }}
              >
                <Pin size={30} />
              </div>
            </Tooltip>
          </Marker>
        ))}
      </ReactMapGL>
    </Box>
  );
};

export { DEFAULT_LATITUDE as ZIPCODE_MAP_DEFAULT_LATITUDE, DEFAULT_LONGITUDE as ZIPCODE_MAP_DEFAULT_LONGITUDE };

export default ZipcodeMapPreview;
